import { isTrackedPath, sanitizeLocation } from "./tracking";

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let measurementId: string | undefined;
let loaded = false;

/** Remembers the measurement ID. Nothing loads until a tracked page is viewed. */
export function initAnalytics(id: string | undefined): void {
  measurementId = id;
}

/**
 * Defines gtag and loads gtag.js once. Calls made before the script arrives
 * wait in dataLayer, so the first page view isn't lost. The automatic page
 * view is off: page views are sent on each route change instead.
 */
function ensureLoaded(): boolean {
  if (!measurementId) return false;
  if (loaded) return true;
  loaded = true;
  window.dataLayer = window.dataLayer ?? [];
  window.gtag = function gtag() {
    // gtag.js expects the Arguments object itself, not an array.
    // oxlint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: false,
    page_location: sanitizeLocation(window.location.href),
  });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.append(script);
  return true;
}

/** Sends a page_view for a tracked page. Does nothing elsewhere or when analytics is off. */
export function trackPageView(href = window.location.href): void {
  const { pathname } = new URL(href);
  if (!isTrackedPath(pathname) || !ensureLoaded()) return;
  window.gtag!("event", "page_view", {
    page_title: document.title,
    page_location: sanitizeLocation(href),
    page_path: pathname,
  });
}

/** GA's recommended lead event, sent after a successful enquiry. No personal details. */
export function trackGenerateLead(service: string | undefined): void {
  if (!ensureLoaded()) return;
  window.gtag!("event", "generate_lead", {
    page_location: sanitizeLocation(window.location.href),
    ...(service ? { service } : {}),
  });
}

export function resetAnalyticsForTests(): void {
  measurementId = undefined;
  loaded = false;
  delete window.gtag;
  delete window.dataLayer;
  document.head.querySelectorAll('script[src*="googletagmanager.com"]').forEach((s) => s.remove());
}
